import {
  SingleFieldConfig,
  FORM_FIELD_TYPES,
  SelectFormFieldConfig,
  MultiSelectFormFieldConfig,
  RadioFormFieldConfig,
  DateRangeFormFieldConfig,
  RepeaterFormFieldConfig,
} from "../FormField/types";

/** Placeholder for empty/unset values */
export const EMPTY_VALUE_PLACEHOLDER = "—";

/**
 * Format a date value for preview display
 */
function formatDateValue(value: unknown): string {
  if (!value) return EMPTY_VALUE_PLACEHOLDER;

  try {
    const date = new Date(value as string | number | Date);
    if (Number.isNaN(date.getTime())) return EMPTY_VALUE_PLACEHOLDER;

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(value);
  }
}

/**
 * Format a date range value for preview display
 */
function formatDateRangeValue(
  value: unknown,
  _config: DateRangeFormFieldConfig
): string {
  if (!value) return EMPTY_VALUE_PLACEHOLDER;

  // Date range is typically stored as an array [startDate, endDate] or object
  if (Array.isArray(value) && value.length === 2) {
    const [start, end] = value;
    const startStr = start
      ? new Date(start).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : EMPTY_VALUE_PLACEHOLDER;
    const endStr = end
      ? new Date(end).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : EMPTY_VALUE_PLACEHOLDER;
    return `${startStr} – ${endStr}`;
  }

  return EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Format a select field value by finding the matching option label
 */
function formatSelectValue(
  value: unknown,
  config: SelectFormFieldConfig
): string {
  if (value === undefined || value === null || value === "")
    return EMPTY_VALUE_PLACEHOLDER;

  const option = config.options.find((opt) => {
    // Handle both simple values and objects with valueKey
    if (config.valueKey) {
      return opt[config.valueKey as keyof typeof opt] === value;
    }
    return opt.id === value;
  });

  return option?.name ? String(option.name) : String(value);
}

/**
 * Format a multi-select field value by finding matching option labels
 */
function formatMultiSelectValue(
  value: unknown,
  config: MultiSelectFormFieldConfig
): string {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  const labels = value
    .map((val) => {
      const option = config.options.find((opt) => {
        if (config.valueKey) {
          return opt[config.valueKey as keyof typeof opt] === val;
        }
        return opt.id === val;
      });
      return option?.name ? String(option.name) : String(val);
    })
    .filter(Boolean);

  return labels.length > 0 ? labels.join(", ") : EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Format a radio field value by finding the matching option label
 */
function formatRadioValue(
  value: unknown,
  config: RadioFormFieldConfig
): string {
  if (value === undefined || value === null || value === "")
    return EMPTY_VALUE_PLACEHOLDER;

  const option = config.options.find(
    (opt) => opt.value === value || opt.id === value
  );

  return option?.label || String(value);
}

/**
 * Format a boolean value (checkbox/toggle) for preview display
 */
function formatBooleanValue(value: unknown): string {
  if (value === undefined || value === null) return EMPTY_VALUE_PLACEHOLDER;
  return value ? "Yes" : "No";
}

/**
 * Format a phone number for preview display
 */
function formatPhoneValue(value: unknown): string {
  if (!value || value === "") return EMPTY_VALUE_PLACEHOLDER;
  return String(value);
}

/**
 * Format a number value for preview display
 */
function formatNumberValue(value: unknown): string {
  if (value === undefined || value === null || value === "")
    return EMPTY_VALUE_PLACEHOLDER;

  const num = Number(value);
  if (Number.isNaN(num)) return EMPTY_VALUE_PLACEHOLDER;

  return num.toLocaleString();
}

/**
 * Format a file field value for preview display
 */
function formatFileValue(value: unknown): string {
  if (!value) return EMPTY_VALUE_PLACEHOLDER;

  if (value instanceof File) {
    return value.name;
  }

  if (typeof value === "string") {
    // Extract filename from URL or path
    const parts = value.split("/");
    return parts[parts.length - 1] || value;
  }

  return EMPTY_VALUE_PLACEHOLDER;
}

/**
 * Format a color value for preview display
 * Returns the hex color value
 */
function formatColorValue(value: unknown): string {
  if (!value || value === "") return EMPTY_VALUE_PLACEHOLDER;
  return String(value).toUpperCase();
}

/**
 * Generic string formatter
 */
function formatStringValue(value: unknown): string {
  if (value === undefined || value === null || value === "")
    return EMPTY_VALUE_PLACEHOLDER;
  return String(value);
}

/**
 * Format a REPEATER field value for preview display
 * Shows count and optionally a summary of key fields
 */
function formatRepeaterValue(
  value: unknown,
  config: RepeaterFormFieldConfig
): string {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return EMPTY_VALUE_PLACEHOLDER;
  }

  const count = value.length;
  const itemLabel = count === 1 ? "item" : "items";

  // Try to create a summary from the first few items
  // Look for common display fields: label, name, title, value, price
  const displayFieldNames = ["label", "name", "title", "value", "price"];
  const firstDisplayField = config.itemFields.find((f) =>
    displayFieldNames.includes(f.name.toLowerCase())
  );

  if (firstDisplayField) {
    const summaries = value.slice(0, 3).map((item) => {
      const fieldValue = item[firstDisplayField.name];
      if (fieldValue === undefined || fieldValue === null) return null;

      // Format currency values
      if (
        firstDisplayField.name === "price" ||
        firstDisplayField.name === "value"
      ) {
        const num = Number(fieldValue);
        if (!Number.isNaN(num)) {
          return `$${num.toFixed(2)}`;
        }
      }

      return String(fieldValue);
    });

    const validSummaries = summaries.filter(Boolean);
    if (validSummaries.length > 0) {
      const summaryText = validSummaries.join(", ");
      const moreText = value.length > 3 ? ` (+${value.length - 3} more)` : "";
      return `${summaryText}${moreText}`;
    }
  }

  return `${count} ${itemLabel}`;
}

/**
 * Format a field value based on its type for preview display
 *
 * @param value - The current field value
 * @param field - The field configuration
 * @returns Formatted string representation of the value
 */
export function formatPreviewValue(
  value: unknown,
  field: SingleFieldConfig | RepeaterFormFieldConfig
): string {
  // Handle REPEATER type
  if (field.type === FORM_FIELD_TYPES.REPEATER) {
    return formatRepeaterValue(value, field as RepeaterFormFieldConfig);
  }

  switch (field.type) {
    case FORM_FIELD_TYPES.CHECKBOX:
    case FORM_FIELD_TYPES.TOGGLE:
      return formatBooleanValue(value);

    case FORM_FIELD_TYPES.SELECT:
      return formatSelectValue(value, field as SelectFormFieldConfig);

    case FORM_FIELD_TYPES.MULTISELECT:
      return formatMultiSelectValue(value, field as MultiSelectFormFieldConfig);

    case FORM_FIELD_TYPES.RADIO:
      return formatRadioValue(value, field as RadioFormFieldConfig);

    case FORM_FIELD_TYPES.DATE:
      return formatDateValue(value);

    case FORM_FIELD_TYPES.DATERANGE:
      return formatDateRangeValue(value, field as DateRangeFormFieldConfig);

    case FORM_FIELD_TYPES.NUMBER:
      return formatNumberValue(value);

    case FORM_FIELD_TYPES.PHONE:
      return formatPhoneValue(value);

    case FORM_FIELD_TYPES.FILE:
    case FORM_FIELD_TYPES.IMAGE:
      return formatFileValue(value);

    case FORM_FIELD_TYPES.COLOR:
      return formatColorValue(value);

    case FORM_FIELD_TYPES.TEXT:
    case FORM_FIELD_TYPES.EMAIL:
    case FORM_FIELD_TYPES.PASSWORD:
    case FORM_FIELD_TYPES.TEXTAREA:
    default:
      return formatStringValue(value);
  }
}

/**
 * Extract all single field configs from a section, flattening groups
 */
export function extractFieldsFromSection(
  fields: SingleFieldConfig[]
): SingleFieldConfig[] {
  return fields.filter((field) => !field.hidden);
}

/**
 * Get the display label for a field
 */
export function getFieldLabel(
  field: SingleFieldConfig | RepeaterFormFieldConfig
): string {
  return field.label || field.name;
}
